<template>
  <div :class="rootClass" :style="rootStyle" :id="rendering?.dataSource">
    <ContentGrid :desktop-default-grid="[2, 20]" :mobile-default-grid="[1, 14]">
      <JssRichText
        v-if="fields?.title?.value"
        tag="h2"
        class="upcoming-title"
        :field="fields?.title"
      />
      <div class="upcoming-tabs">
        <JssRichText
          class="upcoming-tabs-tab"
          v-for="(tab, index) in fields?.list"
          :key="tab?.id"
          :field="tab?.fields?.tabValue"
          :class="{ active: currentIndex === index }"
          @click="currentIndex = index"
        />
      </div>
      <div class="upcoming-list">
        <SiteLink
          class="upcoming-list-item"
          v-for="item in fields?.list[currentIndex]?.fields?.list"
          :key="item?.id"
          :link="item?.fields?.button?.fields?.link"
        >
          <AdaptiveImage
            class="upcoming-list-item-bg"
            :field="item?.fields?.bgImage"
            :show="true"
            :enable-scroll-detection="false"
            :show-image-redius="false"
          >
            <span
              class="upcoming-list-item-date"
              v-html="formatDate(item?.fields?.date?.value)"
            ></span>
          </AdaptiveImage>
          <JssRichText class="upcoming-list-item-title" :field="item?.fields?.title" />
          <JssRichText class="upcoming-list-item-desc" :field="item?.fields?.desc" />
          <siteButton class="upcoming-list-item-btn" v-bind="item?.fields?.button" />
        </SiteLink>
      </div>
    </ContentGrid>
  </div>
</template>

<script>
import { computed, ref, watch, onMounted, nextTick } from 'vue';
import utils from '@/utils';

export default {
  name: 'Upcoming',
  props: {
    fields: {
      type: Object,
      default: () => ({}),
    },
    rendering: {
      type: Object,
      default: () => ({}),
    },
  },
  setup(props) {
    const currentIndex = ref(0);
    const rootClass = computed(() => utils.overall.baseComClass(props.fields, 'upcoming'));

    const rootStyle = ref(utils.overall.baseComStyle(props.fields));
    watch(
      () => props.fields,
      (val) => {
        rootStyle.value = utils.overall.baseComStyle(val);
      },
      { deep: true, immediate: true }
    );
    onMounted(() => {
      nextTick(() => {
        rootStyle.value = { ...utils.overall.baseComStyle(props.fields) };
      });
    });

    const MONTHS_SHORT = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    const formatDate = (dateStr) => {
      if (!dateStr) return '';
      let month, day;
      const match = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (match) {
        month = MONTHS_SHORT[parseInt(match[2], 10) - 1];
        day = parseInt(match[3], 10);
      } else {
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return '';
        month = MONTHS_SHORT[date.getUTCMonth()];
        day = date.getUTCDate();
      }
      return `<span class="upcoming-list-item-date-month">${month}</span><span class="upcoming-list-item-date-day">${day}</span>`;
    };

    return {
      currentIndex,
      rootClass,
      rootStyle,
      formatDate,
    };
  },
};
</script>

<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.upcoming {
  position: relative;
  width: 100%;
  padding: 40px 0;

  &-title {
    font-weight: 700;
  }

  &-tabs {
    display: flex;
    align-items: center;
    gap: 40px;
    margin-top: 40px;
    padding-bottom: 27px;
    overflow-x: auto;
    flex-wrap: nowrap;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }

    &-tab {
      flex-shrink: 0;
      font-size: 16px;
      line-height: 1.4;
      font-weight: 400;
      cursor: pointer;
      position: relative;

      &.active {
        &:after {
          content: '';
          position: absolute;
          bottom: -25px;
          left: 0;
          width: 100%;
          height: 2px;
          background-color: $black;
        }
      }
    }
  }

  &-list {
    display: flex;
    flex-direction: column;
    gap: 24px;
    margin-top: 65px;

    &-item {
      display: flex;
      flex-direction: column;
      gap: 12px;
      padding: 24px;
      border-radius: 16px;
      background-color: $white;

      &-bg {
        width: 100%;
        aspect-ratio: 336 / 190;
        border-radius: 16px;
        overflow: hidden;

        .e-background-image__content {
          display: flex;
          align-items: center;
          justify-content: center;
        }
      }

      &-date {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 10px;
        font-size: 32px;
        line-height: 1.4;
        font-weight: 600;
        color: $white;
      }

      &-title {
        font-size: 16px;
        line-height: 1.4;
        font-weight: 600;
        color: #3e3e3e;
      }

      &-desc {
        font-size: 16px;
        line-height: 1.4;
        font-weight: 400;
        color: #656565;
      }
    }
  }

  @include tablet-landscape {
    padding: 60px 0;

    &-tabs {
      margin-top: 65px;
      padding-bottom: 0;
      overflow: visible;
    }

    &-list {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
    }
  }
}
</style>
